import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';

@Component({
  imports: [
    DotsSpinnerComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    MatErrorComponent,
    MatError,
    IconComponent,
  ],
  selector: 'app-new-password',
  templateUrl: './new-password.component.html',
})
export class NewPasswordComponent {
  public readonly passwordForm = new FormGroup({
    confirmPassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
    password: new FormControl('', [Validators.required, Validators.minLength(6)]),
  });

  public readonly showPassword = signal(false);
  public readonly showConfirmPassword = signal(false);
  public readonly loading = signal(false);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  private oobCode: string | null = null;

  constructor() {
    void this.initFromOobCode();
  }

  private async initFromOobCode(): Promise<void> {
    this.oobCode = this.route.snapshot.queryParamMap.get('oobCode');

    if (!this.oobCode) {
      this.handleInvalidCode();

      return;
    }

    this.loading.set(true);

    try {
      await this.authService.verifyPasswordResetCode(this.oobCode);
    } catch {
      this.handleInvalidCode();
    } finally {
      this.loading.set(false);
    }
  }

  public onSubmit(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();

      return;
    }

    if (this.passwordForm.value.password !== this.passwordForm.value.confirmPassword) {
      this.passwordForm.get('confirmPassword')?.setErrors({ passwordsDoNotMatch: true });

      return;
    }

    this.passwordForm.get('confirmPassword')?.setErrors(null);

    if (!this.oobCode) {
      this.handleInvalidCode();

      return;
    }

    this.loading.set(true);

    void this.authService
      .confirmPasswordReset(this.oobCode, this.passwordForm.value.password!)
      .then(() => {
        void this.router.navigate(['/auth/login'], { state: { afterPasswordReset: true } });
      })
      .catch(() => {
        this.handleResetFailed();
      })
      .finally(() => {
        this.loading.set(false);
      });
  }

  private handleInvalidCode(): void {
    this.snackbarService.openError(this.translate.instant('pages.newPassword.invalidCode'));
    void this.router.navigate(['/auth/forgot-password']);
  }

  private handleResetFailed(): void {
    this.snackbarService.openError(this.translate.instant('pages.newPassword.resetFailed'));
    void this.router.navigate(['/auth/forgot-password']);
  }
}
